import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import restaurantService from '../services/restaurantService'
import cuisineService, { type CuisineDish } from '../services/cuisineService'
import ingredientsService from '../services/ingredientsService'
import menuService from '../services/menuService'
import { Restaurant } from '../types/restaurant'
import toast from 'react-hot-toast'
import { useTranslation } from '../hooks/use-i18n'
import {
  buildDishMarginRows,
  buildIngredientCostRows,
  buildPriceMap,
  mergePricedIngredients,
} from '../utils/costAnalytics'

const RestaurantCosts: React.FC = () => {
  const { t } = useTranslation()
  const { restaurantId } = useParams<{ restaurantId: string }>()
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null)
  const [dishes, setDishes] = useState<CuisineDish[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedIngredientId, setExpandedIngredientId] = useState<string | null>(null)

  useEffect(() => {
    const load = async () => {
      if (!restaurantId) {
        setError(t('error.restaurantIdMissing'))
        setIsLoading(false)
        return
      }
      try {
        setIsLoading(true)
        const rest = await restaurantService.getRestaurant(restaurantId)
        setRestaurant(rest)

        const cuisineIds = rest.cuisines?.map((c) => c.id) || []
        const allDishes: CuisineDish[] = []
        for (const cid of cuisineIds) {
          const categoriesWithDishes = await cuisineService.getDishesByCuisine(cid)
          for (const cat of categoriesWithDishes) {
            for (const d of cat.dishes || []) {
              if (!allDishes.find((x) => x.id === d.id)) allDishes.push(d)
            }
          }
        }
        setDishes(allDishes)
      } catch (err: any) {
        setError(err.response?.data?.message || t('error.costsLoadFailed'))
        toast.error(t('toast.costsLoadFailed'))
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [restaurantId, t])

  const [computed, setComputed] = useState({ costRows: [] as ReturnType<typeof buildIngredientCostRows>, marginRows: [] as ReturnType<typeof buildDishMarginRows>, totalFoodCost: 0 })
  const [pricedLoaded, setPricedLoaded] = useState(false)

  useEffect(() => {
    const compute = async () => {
      if (!restaurantId || dishes.length === 0) return
      const [catalog, mine, menus] = await Promise.all([
        ingredientsService.getCatalog(),
        ingredientsService.getMyIngredients(restaurantId),
        menuService.getMenus(restaurantId),
      ])
      const priced = mergePricedIngredients(catalog, mine)
      const rows = buildIngredientCostRows(dishes, priced)
      const priceById = buildPriceMap(priced)
      const menuPrices = new Map<string, number>()
      for (const menu of menus) {
        for (const item of menu.items ?? []) {
          if (item.name && item.price != null) {
            menuPrices.set(item.name.toLowerCase(), Number(item.price))
          }
        }
      }
      const margins = buildDishMarginRows(dishes, priceById, menuPrices)
      const total = margins.reduce((s, m) => s + m.foodCost, 0)
      setComputed({ costRows: rows, marginRows: margins, totalFoodCost: Math.round(total * 100) / 100 })
      setPricedLoaded(true)
    }
    if (!isLoading && restaurantId) compute()
  }, [restaurantId, dishes, isLoading])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    )
  }

  if (error || !restaurant) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="bg-[#2a2a2a] border border-white/10 rounded-3xl p-8 max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-white mb-4">{t('cost.notFound.title')}</h2>
          <p className="text-white/80 mb-6">{error || t('cost.notFound.defaultMessage')}</p>
          <Link to="/dashboard" className="inline-block bg-black hover:bg-black/80 text-white px-6 py-3 rounded-full font-semibold">
            {t('common.backToDashboard')}
          </Link>
        </div>
      </div>
    )
  }

  const { costRows: rows, marginRows: margins, totalFoodCost } = computed

  return (
    <div className="min-h-screen">
      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        <Link to={`/restaurant/${restaurantId}`} className="inline-flex items-center text-white/80 hover:text-white mb-6">
          {t('common.backWithName', { name: restaurant.name })}
        </Link>
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">{t('cost.title')}</h1>
        <p className="text-white/70 text-sm mb-2">{t('cost.subtitle')}</p>
        <p className="text-amber-400/90 text-xs mb-8">{t('cost.calculatedDisclaimer')}</p>

        {pricedLoaded && (
          <section className="bg-[#2a2a2a] border border-white/10 rounded-2xl p-6 mb-8">
            <p className="text-white/60 text-sm">{t('cost.estimatedFoodCostTotal')}</p>
            <p className="text-3xl font-bold text-white">${totalFoodCost.toFixed(2)}</p>
          </section>
        )}

        <section className="bg-[#2a2a2a] border border-white/10 rounded-2xl p-6 mb-8">
          <h2 className="text-xl font-bold text-white mb-4">{t('cost.dishMargins.title')}</h2>
          {margins.length === 0 ? (
            <p className="text-white/60">{t('cost.dishes.empty')}</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-white/20 text-white/70">
                  <th className="py-2">{t('cost.table.dish')}</th>
                  <th className="py-2">{t('cost.dishMargins.foodCost')}</th>
                  <th className="py-2">{t('cost.dishMargins.menuPrice')}</th>
                  <th className="py-2">{t('cost.dishMargins.margin')}</th>
                </tr>
              </thead>
              <tbody>
                {margins.map((m) => (
                  <tr key={m.dishId} className="border-b border-white/10">
                    <td className="py-2 text-white">{m.dishName}</td>
                    <td className="py-2">${m.foodCost.toFixed(2)}</td>
                    <td className="py-2">{m.menuPrice != null ? `$${m.menuPrice.toFixed(2)}` : '—'}</td>
                    <td className="py-2">
                      {m.marginPct != null ? `${m.marginPct}% ($${m.margin?.toFixed(2)})` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="bg-[#2a2a2a] border border-white/10 rounded-2xl p-6 overflow-x-auto">
          <h2 className="text-xl font-bold text-white mb-4">{t('cost.ingredients.title')}</h2>
          {rows.length === 0 ? (
            <p className="text-white/60">{t('cost.ingredients.empty')}</p>
          ) : (
            <table className="w-full text-left border-collapse text-sm">
              <thead>
                <tr className="border-b border-white/20">
                  <th className="py-3 pr-4">{t('cost.table.ingredient')}</th>
                  <th className="py-3 pr-4">{t('cost.table.unitPrice')}</th>
                  <th className="py-3 pr-4">{t('cost.table.stock')}</th>
                  <th className="py-3 pr-4">{t('cost.table.inRecipes')}</th>
                  <th className="py-3 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <React.Fragment key={row.ingredientId}>
                    <tr className="border-b border-white/10">
                      <td className="py-3 pr-4 font-medium text-white">{row.ingredientName}</td>
                      <td className="py-3 pr-4">${row.unitPrice.toFixed(2)}/{row.unitOfMeasure}</td>
                      <td className="py-3 pr-4">{row.stockQuantity != null ? row.stockQuantity : '—'}</td>
                      <td className="py-3 pr-4">${row.estimatedTotalInRecipes.toFixed(2)}</td>
                      <td className="py-3 pr-4">
                        {row.dishUsage.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setExpandedIngredientId((id) => (id === row.ingredientId ? null : row.ingredientId))}
                            className="text-sky-400 text-sm"
                          >
                            {expandedIngredientId === row.ingredientId ? t('cost.table.hide') : t('cost.table.useByDishes')}
                          </button>
                        )}
                      </td>
                    </tr>
                    {expandedIngredientId === row.ingredientId && (
                      <tr className="bg-white/5">
                        <td colSpan={5} className="py-3 px-4">
                          {row.dishUsage.map((u) => (
                            <div key={u.dishId} className="text-white/80 text-sm">
                              {u.dishName}: ${u.estimatedCostShare.toFixed(2)}
                            </div>
                          ))}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </main>
    </div>
  )
}

export default RestaurantCosts
